/**
 * TablePreview Component
 * Read-only styled table view:
 * - Respects header/footer styling
 * - Shows formatted values
 * - Applies table style settings
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table'
import type { TableHeader as TableHeaderType, TableRow as TableRowType, TableStyle } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface TablePreviewProps {
  headers: TableHeaderType[]
  rows: TableRowType[]
  hasHeaderRow: boolean
  hasFooterRow?: boolean
  style?: TableStyle
  className?: string
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format cell value based on column data type
 */
function formatCellValue(value: string | undefined, dataType: TableHeaderType['dataType']): string {
  if (!value) return '-'

  switch (dataType) {
    case 'Number': {
      const num = parseFloat(value)
      if (isNaN(num)) return value
      return num.toLocaleString('en-US')
    }

    case 'Currency': {
      const num = parseFloat(value)
      if (isNaN(num)) return value
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    }

    case 'Date': {
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-US')
    }

    case 'Boolean': {
      const lower = value.toLowerCase()
      if (lower === 'true' || lower === '1' || lower === 'yes') return 'Yes'
      if (lower === 'false' || lower === '0' || lower === 'no') return 'No'
      return value
    }

    default:
      return value
  }
}

/**
 * Get alignment class
 */
function getAlignmentClass(alignment?: TableHeaderType['alignment']): string {
  switch (alignment) {
    case 'Center':
      return 'text-center'
    case 'Right':
      return 'text-right'
    default:
      return 'text-left'
  }
}

// ============================================
// TablePreview Component
// ============================================

export function TablePreview({
  headers,
  rows,
  hasHeaderRow,
  hasFooterRow = false,
  style,
  className,
}: TablePreviewProps) {
  // Apply style settings
  const tableStyles = useMemo(() => {
    if (!style) return {}

    return {
      '--table-border-color': style.borderColor || '#e5e5e5',
      '--table-header-bg': style.headerBackground || '#f5f5f5',
      '--table-header-text': style.headerTextColor || '#1a1a1a',
      '--table-alt-row-color': style.alternateRowColor || '#fafafa',
      '--table-cell-padding': `${style.cellPadding || 12}px`,
    } as React.CSSProperties
  }, [style])

  // Determine border style
  const borderClass = useMemo(() => {
    if (!style?.borderStyle || style.borderStyle === 'None') {
      return 'border-0'
    }
    if (style.borderStyle === 'Dashed') {
      return 'border-dashed'
    }
    return 'border-solid'
  }, [style?.borderStyle])

  // Get the last row for footer (if enabled)
  const dataRows = hasFooterRow && rows.length > 1 ? rows.slice(0, -1) : rows
  const footerRow = hasFooterRow && rows.length > 1 ? rows[rows.length - 1] : null

  if (headers.length === 0) {
    return (
      <div className={cn('p-8 text-center text-neutral-400', className)}>
        No table data to preview
      </div>
    )
  }

  return (
    <div
      className={cn('overflow-auto', className)}
      style={tableStyles}
    >
      <Table className={cn(borderClass)}>
        {/* Header Row */}
        {hasHeaderRow && (
          <TableHeader>
            <TableRow
              className={cn(
                'bg-[var(--table-header-bg,#f5f5f5)] dark:bg-neutral-800',
                style?.borderStyle !== 'None' && 'border-b-2'
              )}
              style={{
                backgroundColor: style?.headerBackground,
                borderColor: style?.borderColor,
              }}
            >
              {headers.map((header) => (
                <TableHead
                  key={header.key}
                  className={cn(
                    'font-semibold',
                    getAlignmentClass(header.alignment)
                  )}
                  style={{
                    width: header.width ? `${header.width}px` : 'auto',
                    color: style?.headerTextColor,
                    padding: `var(--table-cell-padding, 12px)`,
                  }}
                >
                  {header.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}

        {/* Body Rows */}
        <TableBody>
          {dataRows.map((row, rowIndex) => (
            <TableRow
              key={row.id}
              className={cn(
                style?.alternateRowColor && rowIndex % 2 === 1 && 'bg-[var(--table-alt-row-color,#fafafa)]'
              )}
              style={{
                backgroundColor: style?.alternateRowColor && rowIndex % 2 === 1
                  ? style.alternateRowColor
                  : undefined,
                borderColor: style?.borderColor,
              }}
            >
              {headers.map((header) => {
                const cell = row.cells[header.key]
                const displayValue = cell?.displayValue || formatCellValue(cell?.value, header.dataType)
                const isFormula = cell?.isFormula || (cell?.value || '').startsWith('=')

                return (
                  <TableCell
                    key={header.key}
                    className={cn(
                      getAlignmentClass(header.alignment),
                      (header.dataType === 'Number' || header.dataType === 'Currency') && 'font-mono',
                      isFormula && 'text-purple-600 dark:text-purple-400 italic'
                    )}
                    style={{
                      padding: `var(--table-cell-padding, 12px)`,
                      borderColor: style?.borderColor,
                    }}
                  >
                    {displayValue || <span className="text-neutral-300">-</span>}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>

        {/* Footer Row */}
        {hasFooterRow && footerRow && (
          <TableFooter>
            <TableRow
              className={cn(
                'bg-neutral-100 dark:bg-neutral-800/50 font-medium',
                style?.borderStyle !== 'None' && 'border-t-2'
              )}
              style={{ borderColor: style?.borderColor }}
            >
              {headers.map((header) => {
                const cell = footerRow.cells[header.key]
                const displayValue = cell?.displayValue || formatCellValue(cell?.value, header.dataType)

                return (
                  <TableCell
                    key={header.key}
                    className={cn(
                      getAlignmentClass(header.alignment),
                      (header.dataType === 'Number' || header.dataType === 'Currency') && 'font-mono'
                    )}
                    style={{
                      padding: `var(--table-cell-padding, 12px)`,
                      borderColor: style?.borderColor,
                    }}
                  >
                    {displayValue || <span className="text-neutral-300">-</span>}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableFooter>
        )}
      </Table>

      {/* Empty State */}
      {rows.length === 0 && (
        <div className="p-8 text-center text-neutral-400">
          No rows to display. Add rows to see the preview.
        </div>
      )}
    </div>
  )
}

export default TablePreview
